// src/pages/TextTools/MindMap.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
    generateMindMap, 
    checkMindMapStatus, 
    downloadMindMapImage,
    downloadMindMapJSON 
} from '../../services/chatApi';
import { Download, FileText, Loader2, Upload, Plus, Trash2, Edit3, RotateCcw, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};

const MindMap = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    const [inputMethod, setInputMethod] = useState('none');
    const [subject, setSubject] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);
    const [mindMapData, setMindMapData] = useState(null);
    const [originalMindMapData, setOriginalMindMapData] = useState(null);
    const [rootTitle, setRootTitle] = useState('');
    const [originalRootTitle, setOriginalRootTitle] = useState('');
    const [mindMapImage, setMindMapImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [taskId, setTaskId] = useState(null);
    const [taskStatus, setTaskStatus] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [showColors, setShowColors] = useState(true);
    const [resetSuccess, setResetSuccess] = useState(false);

    // progresss bar
    const [progress, setProgress] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');



    // Effect to poll task status
    useEffect(() => {
        let interval = null;
        if (taskId && taskStatus === 'processing' || taskStatus === 'PROGRESS') {
            interval = setInterval(async () => {
                try {
                    const statusResponse = await checkMindMapStatus(taskId);
                    const status = statusResponse.status;
                    setTaskStatus(status);
                    if (status === 'processing' || status === 'PROGRESS') {
                        // Update progress based on backend response
                        setProgress(statusResponse.progress !== undefined ? statusResponse.progress : 0);
                        setProgressMessage(statusResponse.message || t('mindMap.processing'));
                    } else if (status === 'completed') {
                        setMindMapData(statusResponse.mind_map_structure);
                        setOriginalMindMapData(JSON.parse(JSON.stringify(statusResponse.mind_map_structure)));
                        setRootTitle(statusResponse.root_title);
                        setOriginalRootTitle(statusResponse.root_title);
                        setMindMapImage(statusResponse.image_url);
                        setProgress(100);
                        setProgressMessage(t('mindMap.completedMindmap'));
                        clearInterval(interval);
                        
                        if (statusResponse.new_credits !== undefined) {
                            setCredits(statusResponse.new_credits);
                        }
                        
                        setTaskId(null);
                        setIsLoading(false);
                        setTaskStatus(null);
                    } else if (statusResponse.status === 'failed') {
                        setError(statusResponse.error || t('mindMap.errorGeneric'));
                        clearInterval(interval);
                        setTaskId(null);
                        setIsLoading(false);
                        setTaskStatus(null);
                        setProgress(0); // Reset progress on failure
                        setProgressMessage('');
                    }
                } catch (err) {
                    console.error('Error checking task status:', err);
                    clearInterval(interval);
                    setTaskId(null);
                    setError(t('mindMap.statusCheckError'));
                    setIsLoading(false);
                    setTaskStatus(null);
                    setProgress(0);
                    setProgressMessage('');
                }
            }, 2000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [taskId, taskStatus, t, setCredits]);

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                setError(t('mindMap.fileTooLarge'));
                return;
            }
            setUploadedFile(file);
            setError(null);
            // Reset everything when new file is uploaded
            
        }
    };

    const handleGenerate = async () => {
        setError(null);
        setMindMapData(null);
        setMindMapImage(null);
        setTaskId(null);
        setTaskStatus(null);
        setProgress(0); // Reset progress
        setProgressMessage('');

        if (inputMethod === 'file' && !uploadedFile) {
            setError(t('mindMap.uploadFileFirst'));
            return;
        }

        if (inputMethod === 'text' && !subject.trim()) {
            setError(t('mindMap.enterSubjectFirst'));
            return;
        }

        setIsLoading(true);

        try {
            const formData = new FormData();
            formData.append('input_method', inputMethod);

            if (inputMethod === 'file') {
                formData.append('file', uploadedFile);
            } else {
                formData.append('subject', subject);
            }

            const result = await generateMindMap(formData);
            
            if (result.task_id) {
                setTaskId(result.task_id);
                setTaskStatus('processing');
                setProgressMessage(t('mindMap.startingMindmap') || 'Starting mind map generation...');
            } else {
                // If no task_id is returned, something went wrong
                setError(t('mindMap.errorGeneric'));
                setIsLoading(false);
                setTaskStatus(null);
                // setProgress(0);
                // setProgressMessage('');
                }
        } catch (err) {
            setError(err.message || t('mindMap.errorGeneric'));
            setIsLoading(false);
            setTaskStatus(null);
            setProgress(0);
            setProgressMessage('');
        }
    };

    const handleRegenerateMindMap = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
            const blob = await downloadMindMapImage(mindMapData, rootTitle, showColors);
            const imageUrl = URL.createObjectURL(blob);
            setMindMapImage(imageUrl);
        } catch (err) {
            setError(t('mindMap.regenerateError'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadImage = async () => {
        try {
            const blob = await downloadMindMapImage(mindMapData, rootTitle, showColors);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'mindmap.png';
            document.body.appendChild(a);
            a.click();
            URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            setError(t('mindMap.downloadError'));
        }
    };

    const handleDownloadJSON = () => {
        try {
            const jsonData = {
                root_title: rootTitle,
                structure: mindMapData
            };
            const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'mindmap.json';
            document.body.appendChild(a);
            a.click();
            URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            setError(t('mindMap.downloadError'));
        }
    };

    const handleClear = () => {
        setSubject('');
        setUploadedFile(null);
        setMindMapData(null);
        setOriginalMindMapData(null);
        setMindMapImage(null);
        setRootTitle('');
        setOriginalRootTitle('');
        setError(null);
        setInputMethod('none');
        setTaskId(null);
        setTaskStatus(null);
        setActiveTab('overview');
        setProgress(0); // Reset progress when clearing
        setProgressMessage('');
    };

    const handleResetToOriginal = () => {
        if (originalMindMapData) {
            setMindMapData(JSON.parse(JSON.stringify(originalMindMapData)));
            setRootTitle(originalRootTitle);
            setResetSuccess(true);
            setTimeout(() => setResetSuccess(false), 2000); // Show success for 2 seconds
        }
    };

    // Editing functions
    const handleAddMainConcept = (newMainConcept, subConcepts) => {
        const updatedData = { ...mindMapData };
        updatedData[newMainConcept] = subConcepts;
        setMindMapData(updatedData);
    };

    const handleDeleteMainConcept = (mainConcept) => {
        const updatedData = { ...mindMapData };
        delete updatedData[mainConcept];
        setMindMapData(updatedData);
    };

    const handleEditMainConcept = (oldName, newName) => {
        const updatedData = { ...mindMapData };
        updatedData[newName] = updatedData[oldName];
        delete updatedData[oldName];
        setMindMapData(updatedData);
    };

    const handleAddSubConcept = (mainConcept, subConcept, details) => {
        const updatedData = { ...mindMapData };
        updatedData[mainConcept][subConcept] = details;
        setMindMapData(updatedData);
    };

    const handleDeleteSubConcept = (mainConcept, subConcept) => {
        const updatedData = { ...mindMapData };
        delete updatedData[mainConcept][subConcept];
        setMindMapData(updatedData);
    };

    const handleEditSubConcept = (mainConcept, oldName, newName, newDetails) => {
        const updatedData = { ...mindMapData };
        updatedData[mainConcept][newName] = newDetails;
        if (oldName !== newName) {
            delete updatedData[mainConcept][oldName];
        }
        setMindMapData(updatedData);
    };

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : (
                <div className="max-w-7xl mx-auto">
                    <h3 className="text-3xl font-bold text-brand-orange">
                        {t('mindMap.title')}
                    </h3>
                    <p className="mt-2 text-brand-dark">
                        {t('mindMap.description')}
                    </p>
                    
                    {/* Input Method Selection */}
                    {!mindMapData && (
                        <>
                            <div className="mt-8">
                                <label className="block text-sm font-medium mb-2">
                                    {t('mindMap.inputMethodLabel')}
                                </label>
                                <select
                                    value={inputMethod}
                                    onChange={(e) => {
                                        setInputMethod(e.target.value);
                                        setError(null);
                                    }}
                                    className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none rounded-md"
                                    disabled={isLoading}
                                >
                                    <option value="none">{t('mindMap.selectMethod')}</option>
                                    <option value="file">{t('mindMap.uploadFile')}</option>
                                    <option value="text">{t('mindMap.typeSubject')}</option>
                                </select>
                            </div>

                            {/* File Upload Section */}
                            {inputMethod === 'file' && (
                                <div className="mt-6">
                                    <label className="block text-sm font-medium mb-2">
                                        {t('mindMap.fileUploadLabel')}
                                    </label>
                                    <div className="flex items-center space-x-4">
                                        <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-slate-600 rounded-md cursor-pointer hover:border-brand-orange transition-colors">
                                            <Upload className="mr-2" size={20} />
                                            <span>{uploadedFile ? uploadedFile.name : t('mindMap.chooseFile')}</span>
                                            <input
                                                type="file"
                                                accept=".pdf,.docx,.txt"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                                disabled={isLoading}
                                            />
                                        </label>
                                    </div>
                                    <p className="text-xs text-slate-600 mt-1">
                                        {t('mindMap.fileFormats')}
                                    </p>
                                </div>
                            )}

                            {/* Text Input Section */}
                            {inputMethod === 'text' && (
                                <div className="mt-6">
                                    <label className="block text-sm font-medium mb-2">
                                        {t('mindMap.subjectLabel')}
                                    </label>
                                    <textarea
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        placeholder={t('mindMap.subjectPlaceholder')}
                                        className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none min-h-[200px] resize-y rounded-md"
                                        disabled={isLoading}
                                    />
                                </div>
                            )}

                            {error && (
                                <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                                    {error}
                                </div>
                            )}

                            {(taskStatus === 'processing' || taskStatus === 'PROGRESS') && (
                                <div className="mt-6">
                                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                                        <div
                                            className="bg-brand-orange h-2.5 rounded-full transition-all duration-300 ease-in-out"
                                            style={{ width: `${progress}%` }}
                                        ></div>
                                    </div>
                                    <div className="mt-2 text-sm text-brand-dark font-medium">
                                        {progressMessage} ({Math.round(progress)}%)
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            {inputMethod !== 'none' && (
                                <div className="flex items-center space-x-4 mt-6">
                                    <button
                                        onClick={handleGenerate}
                                        className="px-6 py-2 border border-brand-orange hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors disabled:bg-slate-500 disabled:cursor-not-allowed flex items-center"
                                        disabled={isLoading || taskStatus === 'processing' || taskStatus === 'PROGRESS'}
                                    >
                                        {isLoading || taskStatus === 'processing' || taskStatus === 'PROGRESS' ? (
                                            <>
                                                <Loader2 className="animate-spin mr-2" size={20} />
                                                {t('mindMap.generatingButton')}
                                            </>
                                        ) : (
                                            t('mindMap.generateButton')
                                        )}
                                    </button>
                                    <button
                                        onClick={handleClear}
                                        className="px-6 py-2 border border-gray-600 hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors"
                                        disabled={isLoading || taskStatus === 'processing' || taskStatus === 'PROGRESS'}
                                    >
                                        {t('mindMap.clearButton')}
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {/* Mind Map Editor and Display */}
                    {mindMapData && (
                        <MindMapEditor
                            mindMapData={mindMapData}
                            setMindMapData={setMindMapData}
                            rootTitle={rootTitle}
                            setRootTitle={setRootTitle}
                            mindMapImage={mindMapImage}
                            showColors={showColors}
                            setShowColors={setShowColors}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            onRegenerate={handleRegenerateMindMap}
                            onDownloadImage={handleDownloadImage}
                            onDownloadJSON={handleDownloadJSON}
                            onReset={handleResetToOriginal}
                            onClear={handleClear}
                            isLoading={isLoading}
                            resetSuccess={resetSuccess} // New prop
                            t={t}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

// Separate component for the editor
const MindMapEditor = ({ 
    mindMapData, 
    setMindMapData, 
    rootTitle, 
    setRootTitle,
    mindMapImage,
    showColors,
    setShowColors,
    activeTab,
    setActiveTab,
    onRegenerate,
    onDownloadImage,
    onDownloadJSON,
    onReset,
    onClear,
    isLoading,
    resetSuccess, // New prop
    t
}) => {
    const [editingMain, setEditingMain] = useState('');
    const [editingSub, setEditingSub] = useState('');
    const [newMainName, setNewMainName] = useState('');
    const [newSubName, setNewSubName] = useState('');
    const [newDetails, setNewDetails] = useState('');
    const [selectedMain, setSelectedMain] = useState('');

    const tabs = [
        { id: 'overview', label: t('mindMap.overviewTab'), icon: FileText },
        { id: 'editor', label: t('mindMap.editorTab'), icon: Edit3 },
        { id: 'add', label: t('mindMap.addTab'), icon: Plus },
        { id: 'delete', label: t('mindMap.deleteTab'), icon: Trash2 },
    ];

    return (
        <div className="mt-8">
            {/* Mind Map Image */}
            {mindMapImage && (
                <div className="mb-6">
                    <img src={mindMapImage} alt="Mind Map" className="w-full border border-slate-300 rounded-lg" />
                </div>
            )}

            {/* Controls */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            checked={showColors}
                            onChange={(e) => setShowColors(e.target.checked)}
                            className="form-checkbox"
                        />
                        <span>{t('mindMap.showColors')}</span>
                    </label>
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={onRegenerate}
                        className="px-4 py-2 bg-brand-orange text-white rounded-md hover:bg-orange-600 transition-colors flex items-center"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <Loader2 className="animate-spin mr-2" size={16} />
                        ) : (
                            <RotateCcw className="mr-2" size={16} />
                        )}
                        {t('mindMap.regenerateButton')}
                    </button>
                    <button
                        onClick={onDownloadImage}
                        className="px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-brand-orange transition-colors flex items-center"
                    >
                        <Download size={16} className="mr-2" />
                        {t('mindMap.downloadImage')}
                    </button>
                    <button
                        onClick={onDownloadJSON}
                        className="px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-brand-orange transition-colors flex items-center"
                    >
                        <Download size={16} className="mr-2" />
                        {t('mindMap.downloadJSON')}
                    </button>
                    <button
                        onClick={onReset}
                        className={`px-4 py-2 text-white rounded-md transition-colors flex items-center ${resetSuccess ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-400 hover:bg-brand-orange'}`}
                    >
                        {resetSuccess ? (
                            <Check size={16} className="mr-2" />
                        ) : null}
                        {t('mindMap.resetButton')}
                    </button>
                    <button
                        onClick={onClear}
                        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                    >
                        {t('mindMap.clearButton')}
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-slate-300 mb-6">
                <div className="flex space-x-1">
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-4 py-2 font-medium transition-colors flex items-center ${
                                    activeTab === tab.id
                                        ? 'border-b-2 border-brand-orange text-brand-orange'
                                        : 'text-slate-600 hover:text-brand-orange'
                                }`}
                            >
                                <Icon size={16} className="mr-2" />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Tab Content */}
            <div className="bg-slate-50 p-6 rounded-lg">
                {activeTab === 'overview' && (
                    <OverviewTab mindMapData={mindMapData} rootTitle={rootTitle} t={t} />
                )}
                {activeTab === 'editor' && (
                    <EditorTab 
                        mindMapData={mindMapData} 
                        setMindMapData={setMindMapData}
                        rootTitle={rootTitle}
                        setRootTitle={setRootTitle}
                        t={t} 
                    />
                )}
                {activeTab === 'add' && (
                    <AddTab 
                        mindMapData={mindMapData} 
                        setMindMapData={setMindMapData}
                        t={t} 
                    />
                )}
                {activeTab === 'delete' && (
                    <DeleteTab 
                        mindMapData={mindMapData} 
                        setMindMapData={setMindMapData}
                        t={t} 
                    />
                )}
            </div>
        </div>
    );
};

// Overview Tab
// Overview Tab
const OverviewTab = ({ mindMapData, rootTitle, t }) => {
    return (
        <div>
            <h4 className="text-xl font-bold mb-4 text-brand-orange">{rootTitle}</h4>
            {/* Iterate over main concepts */}
            {Object.entries(mindMapData).map(([mainConcept, subConcepts]) => (
                <div key={mainConcept} className="mb-4">
                    <h5 className="font-bold text-green-600 mb-2">{mainConcept}</h5>
                    {/* Iterate over sub-concepts within the current main concept */}
                    {Object.entries(subConcepts).map(([subConcept, details]) => (
                        <div key={subConcept} className="ml-4 mb-2">
                            <h6 className="font-semibold text-yellow-600">{subConcept}</h6>
                            <ul className="ml-4 list-disc text-blue-600">
                                {/* Add a check to ensure 'details' is an array before mapping */}
                                {Array.isArray(details) ? (
                                    details.map((detail, idx) => (
                                        <li key={idx}>{detail}</li>
                                    ))
                                ) : (
                                    // Fallback if 'details' is not an array
                                    <li className="text-red-500 italic">Error: Details format is invalid.</li>
                                )}
                            </ul>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};
// Editor Tab
// Editor Tab
const EditorTab = ({ mindMapData, setMindMapData, rootTitle, setRootTitle, t }) => {
    // Initialize localData with stable IDs for sub-concepts
    const [localData, setLocalData] = useState(() => {
        const initializedData = {};
        Object.entries(mindMapData).forEach(([mainConcept, subConceptsObj]) => {
            const subConceptsWithIds = {};
            Object.entries(subConceptsObj).forEach(([name, details], index) => {
                // Generate a simple ID based on original name and index for initial state
                // In a more complex app, you might use a proper UUID library
                subConceptsWithIds[`__sub_id_${mainConcept}_${name}_${index}`] = {
                    name: name,
                    details: details
                };
            });
            initializedData[mainConcept] = subConceptsWithIds;
        });
        return initializedData;
    });

    const [localRoot, setLocalRoot] = useState(rootTitle);
    const [editingMainConcept, setEditingMainConcept] = useState(null);
    const [editingMainConceptValue, setEditingMainConceptValue] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);

    const handleSave = () => {
        // Convert localData structure back to the original mindMapData structure (name -> details array)
        const convertedData = {};
        Object.entries(localData).forEach(([mainConcept, subConceptsObj]) => {
            const subConceptsByName = {};
            Object.values(subConceptsObj).forEach((subItem) => { // Iterate values (the objects with name/details)
                subConceptsByName[subItem.name] = subItem.details;
            });
            convertedData[mainConcept] = subConceptsByName;
        });
        setMindMapData(convertedData);
        setRootTitle(localRoot);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2000); // Show success for 2 seconds
    };

    const handleMainConceptChange = (oldName, newName) => {
        if (oldName === newName) return; // No change needed
        const updated = {};
        // Preserve the order by iterating through existing keys
        Object.keys(localData).forEach(key => {
            if (key === oldName) {
                updated[newName] = localData[oldName];
            } else {
                updated[key] = localData[key];
            }
        });
        setLocalData(updated);
    };

    // Function to generate a unique ID (simplified, consider a UUID library for production)
    const generateId = () => `__id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const handleSubConceptChange = (mainConcept, subConceptId, newName, newDetailsStr) => {
        const updated = { ...localData };
        const subConcepts = { ...updated[mainConcept] };
        const detailsArray = newDetailsStr.split(',').map(d => d.trim()).filter(d => d);

        // Update the specific sub-concept by its ID
        if (subConcepts[subConceptId]) {
            subConcepts[subConceptId] = {
                name: newName,
                details: detailsArray
            };
        }
        updated[mainConcept] = subConcepts;
        setLocalData(updated);
    };

    // const handleAddSubConcept = (mainConcept, initialName = "New Sub-Concept") => {
    //     const updated = { ...localData };
    //     const newId = generateId();
    //     if (!updated[mainConcept]) {
    //         updated[mainConcept] = {};
    //     }
    //     updated[mainConcept][newId] = {
    //         name: initialName,
    //         details: ["Detail 1"] // Add default detail or empty array
    //     };
    //     setLocalData(updated);
    // };

    // const handleDeleteSubConcept = (mainConcept, subConceptId) => {
    //     const updated = { ...localData };
    //     const subConcepts = { ...updated[mainConcept] };
    //     delete subConcepts[subConceptId];
    //     updated[mainConcept] = subConcepts;
    //     setLocalData(updated);
    // };

    return (
        <div>
            <div className="mb-6">
                <label className="block text-sm font-medium mb-2">{t('mindMap.rootTitleLabel')}</label>
                <input
                    type="text"
                    value={localRoot}
                    onChange={(e) => setLocalRoot(e.target.value)}
                    className="w-full p-3 border border-slate-300 rounded-md"
                />
            </div>
            {Object.entries(localData).map(([mainConcept, subConceptsObj]) => (
                <div key={mainConcept} className="mb-6 p-4 border border-slate-300 rounded-md">
                    <div className="flex items-center justify-between mb-4"> {/* Added flex container for main concept */}
                        <input
                            type="text"
                            value={editingMainConcept === mainConcept ? editingMainConceptValue : mainConcept}
                            onChange={(e) => setEditingMainConceptValue(e.target.value)}
                            onFocus={() => {
                                setEditingMainConcept(mainConcept);
                                setEditingMainConceptValue(mainConcept);
                            }}
                            onBlur={() => {
                                if (editingMainConceptValue.trim() && editingMainConceptValue !== mainConcept) {
                                    handleMainConceptChange(mainConcept, editingMainConceptValue);
                                }
                                setEditingMainConcept(null);
                                setEditingMainConceptValue('');
                            }}
                            className="p-2 border border-slate-300 rounded-md font-bold flex-grow mr-2" // Added flex-grow and margin
                        />
                    </div>
                    {Object.entries(subConceptsObj).map(([subConceptId, subConceptData]) => (
                        <div key={subConceptId} className="ml-4 mb-4 grid grid-cols-[1fr_2fr_auto] gap-4 items-start"> {/* Changed grid-cols and added items-start */}
                            <input
                                type="text"
                                value={subConceptData.name}
                                onChange={(e) => handleSubConceptChange(mainConcept, subConceptId, e.target.value, Array.isArray(subConceptData.details) ? subConceptData.details.join(', ') : '')}
                                className="p-2 border border-slate-300 rounded-md"
                            />
                            <input
                                value={Array.isArray(subConceptData.details) ? subConceptData.details.join(', ') : ''}
                                onChange={(e) => handleSubConceptChange(mainConcept, subConceptId, subConceptData.name, e.target.value)}
                                className="p-2 border border-slate-300 rounded-md"
                                rows="2"
                            />
                        </div>
                    ))}
                </div>
            ))}
            <button
                onClick={handleSave}
                className={`px-6 py-2 text-white rounded-md transition-colors flex items-center ${saveSuccess ? 'bg-green-500 hover:bg-green-600' : 'bg-brand-orange hover:bg-orange-600'}`}
            >
                {saveSuccess ? (
                    <Check size={20} className="mr-2" />
                ) : null}
                {t('mindMap.saveChanges')}
            </button>
        </div>
    );
};


// Add Tab
const AddTab = ({ mindMapData, setMindMapData, t }) => {
    const [newMainConcept, setNewMainConcept] = useState('');
    const [selectedMain, setSelectedMain] = useState('');
    const [newSubConcept, setNewSubConcept] = useState('');
    const [newDetails, setNewDetails] = useState('');

    const handleAddMainConcept = () => {
        if (newMainConcept.trim()) {
            const updated = { ...mindMapData };
            updated[newMainConcept] = {};
            setMindMapData(updated);
            setNewMainConcept('');
        }
    };

    const handleAddSubConcept = () => {
        if (selectedMain && newSubConcept.trim()) {
            const updated = { ...mindMapData };
            const detailsArray = newDetails.split(',').map(d => d.trim()).filter(d => d);
            updated[selectedMain][newSubConcept] = detailsArray;
            setMindMapData(updated);
            setNewSubConcept('');
            setNewDetails('');
        }
    };

    return (
        <div className="space-y-6">
            <div className="p-4 border border-slate-300 rounded-md">
                <h4 className="font-bold mb-4">{t('mindMap.addMainConcept')}</h4>
                <input
                    type="text"
                    value={newMainConcept}
                    onChange={(e) => setNewMainConcept(e.target.value)}
                    placeholder={t('mindMap.mainConceptPlaceholder')}
                    className="w-full p-2 border border-slate-300 rounded-md mb-4"
                />
                <button
                    onClick={handleAddMainConcept}
                    className="px-4 py-2 bg-brand-orange text-white rounded-md hover:bg-orange-600 transition-colors"
                >
                    <Plus size={16} className="inline mr-2" />
                    {t('mindMap.addButton')}
                </button>
            </div>

            <div className="p-4 border border-slate-300 rounded-md">
                <h4 className="font-bold mb-4">{t('mindMap.addSubConcept')}</h4>
                <select
                    value={selectedMain}
                    onChange={(e) => setSelectedMain(e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-md mb-4"
                >
                    <option value="">{t('mindMap.selectMainConcept')}</option>
                    {Object.keys(mindMapData).map(main => (
                        <option key={main} value={main}>{main}</option>
                    ))}
                </select>
                <input
                    type="text"
                    value={newSubConcept}
                    onChange={(e) => setNewSubConcept(e.target.value)}
                    placeholder={t('mindMap.subConceptPlaceholder')}
                    className="w-full p-2 border border-slate-300 rounded-md mb-4"
                />
                <textarea
                    value={newDetails}
                    onChange={(e) => setNewDetails(e.target.value)}
                    placeholder={t('mindMap.detailsPlaceholder')}
                    className="w-full p-2 border border-slate-300 rounded-md mb-4"
                    rows="3"
                />
                <button
                    onClick={handleAddSubConcept}
                    className="px-4 py-2 bg-brand-orange text-white rounded-md hover:bg-orange-600 transition-colors"
                >
                    <Plus size={16} className="inline mr-2" />
                    {t('mindMap.addButton')}
                </button>
            </div>
        </div>
    );
};

// Delete Tab
const DeleteTab = ({ mindMapData, setMindMapData, t }) => {
    const [deleteType, setDeleteType] = useState('main');
    const [selectedMain, setSelectedMain] = useState('');
    const [selectedSub, setSelectedSub] = useState('');
    const [selectedDetail, setSelectedDetail] = useState('');

    const handleDeleteMain = () => {
        if (selectedMain) {
            const updated = { ...mindMapData };
            delete updated[selectedMain];
            setMindMapData(updated);
            setSelectedMain('');
        }
    };

    const handleDeleteSub = () => {
        if (selectedMain && selectedSub) {
            const updated = { ...mindMapData };
            delete updated[selectedMain][selectedSub];
            setMindMapData(updated);
            setSelectedSub('');
        }
    };

    const handleDeleteDetail = () => {
        if (selectedMain && selectedSub && selectedDetail) {
            const updated = { ...mindMapData };
            updated[selectedMain][selectedSub] = updated[selectedMain][selectedSub].filter(
                d => d !== selectedDetail
            );
            setMindMapData(updated);
            setSelectedDetail('');
        }
    };

    return (
        <div className="space-y-6">
            <div className="mb-4">
                <label className="block text-sm font-medium mb-2">{t('mindMap.deleteTypeLabel')}</label>
                <select
                    value={deleteType}
                    onChange={(e) => {
                        setDeleteType(e.target.value);
                        setSelectedMain('');
                        setSelectedSub('');
                        setSelectedDetail('');
                    }}
                    className="w-full p-2 border border-slate-300 rounded-md"
                >
                    <option value="main">{t('mindMap.deleteMain')}</option>
                    <option value="sub">{t('mindMap.deleteSub')}</option>
                    <option value="detail">{t('mindMap.deleteDetail')}</option>
                </select>
            </div>

            {deleteType === 'main' && (
                <div className="p-4 border border-slate-300 rounded-md">
                    <select
                        value={selectedMain}
                        onChange={(e) => setSelectedMain(e.target.value)}
                        className="w-full p-2 border border-slate-300 rounded-md mb-4"
                    >
                        <option value="">{t('mindMap.selectMainConcept')}</option>
                        {Object.keys(mindMapData).map(main => (
                            <option key={main} value={main}>{main}</option>
                        ))}
                    </select>
                    <button
                        onClick={handleDeleteMain}
                        disabled={!selectedMain}
                        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors disabled:bg-gray-400"
                    >
                        <Trash2 size={16} className="inline mr-2" />
                        {t('mindMap.deleteButton')}
                    </button>
                </div>
            )}

            {deleteType === 'sub' && (
                <div className="p-4 border border-slate-300 rounded-md">
                    <select
                        value={selectedMain}
                        onChange={(e) => {
                            setSelectedMain(e.target.value);
                            setSelectedSub('');
                        }}
                        className="w-full p-2 border border-slate-300 rounded-md mb-4"
                    >
                        <option value="">{t('mindMap.selectMainConcept')}</option>
                        {Object.keys(mindMapData).map(main => (
                            <option key={main} value={main}>{main}</option>
                        ))}
                    </select>
                    {selectedMain && (
                        <>
                            <select
                                value={selectedSub}
                                onChange={(e) => setSelectedSub(e.target.value)}
                                className="w-full p-2 border border-slate-300 rounded-md mb-4"
                            >
                                <option value="">{t('mindMap.selectSubConcept')}</option>
                                {Object.keys(mindMapData[selectedMain] || {}).map(sub => (
                                    <option key={sub} value={sub}>{sub}</option>
                                ))}
                            </select>
                            <button
                                onClick={handleDeleteSub}
                                disabled={!selectedSub}
                                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors disabled:bg-gray-400"
                            >
                                <Trash2 size={16} className="inline mr-2" />
                                {t('mindMap.deleteButton')}
                            </button>
                        </>
                    )}
                </div>
            )}

            {deleteType === 'detail' && (
                <div className="p-4 border border-slate-300 rounded-md">
                    <select
                        value={selectedMain}
                        onChange={(e) => {
                            setSelectedMain(e.target.value);
                            setSelectedSub('');
                            setSelectedDetail('');
                        }}
                        className="w-full p-2 border border-slate-300 rounded-md mb-4"
                    >
                        <option value="">{t('mindMap.selectMainConcept')}</option>
                        {Object.keys(mindMapData).map(main => (
                            <option key={main} value={main}>{main}</option>
                        ))}
                    </select>
                    {selectedMain && (
                        <select
                            value={selectedSub}
                            onChange={(e) => {
                                setSelectedSub(e.target.value);
                                setSelectedDetail('');
                            }}
                            className="w-full p-2 border border-slate-300 rounded-md mb-4"
                        >
                            <option value="">{t('mindMap.selectSubConcept')}</option>
                            {Object.keys(mindMapData[selectedMain] || {}).map(sub => (
                                <option key={sub} value={sub}>{sub}</option>
                            ))}
                        </select>
                    )}
                    {selectedMain && selectedSub && (
                        <>
                            <select
                                value={selectedDetail}
                                onChange={(e) => setSelectedDetail(e.target.value)}
                                className="w-full p-2 border border-slate-300 rounded-md mb-4"
                            >
                                <option value="">{t('mindMap.selectDetail')}</option>
                                {(mindMapData[selectedMain]?.[selectedSub] || []).map((detail, idx) => (
                                    <option key={idx} value={detail}>{detail}</option>
                                ))}
                            </select>
                            <button
                                onClick={handleDeleteDetail}
                                disabled={!selectedDetail}
                                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors disabled:bg-gray-400"
                            >
                                <Trash2 size={16} className="inline mr-2" />
                                {t('mindMap.deleteButton')}
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default MindMap;
